import { cache } from 'utils/react-canary';
import { v4 } from 'uuid';
import { createHash } from 'crypto';
import { IncomingHttpHeaders } from 'http';
import { REQUEST_ID_HEADER } from '../constants/headers';

const generateNewRequestId = cache(() => v4());

/**
 * Generates a predictable 32-character long hash in v4-UUID format from an input string.
 *
 * @param {string} input
 * @returns A 32-character long string in v4-UUID format. Note that this is not a random UUID .
 */
const getUIDHash = (input: string) => {
  // Step 1: Hash the input string using SHA-256
  const hash = createHash('sha256').update(input).digest('hex');
  const part1 = hash.substring(0, 8);
  const part2 = hash.substring(8, 12);
  const part3 = hash.substring(12, 15);
  const part4 = hash.substring(15, 19);
  const part5 = hash.substring(19, 31);
  return `${part1}-${part2}-4${part3}-${part4}-${part5}`;
};

/**
 * A memoized method that returns a request id to be added to requests to distributed Caterpillar systems.
 * A single id will be generated for a request/response cycle. Pass headers to reuse an existing incoming
 * request id from a request.
 * @param headers headers to extract an existing request id from
 * @returns A request id
 */
export const getRequestId = (headers?: IncomingHttpHeaders | Headers) => {
  const existingRequestHeader =
    headers?.[REQUEST_ID_HEADER] ||
    (!!headers?.get && (headers as Headers)?.get(REQUEST_ID_HEADER));
  return existingRequestHeader || generateNewRequestId();
};

/**
 * Generates a stable requestId based on the URL passed. Stable requestIds are used to help
 * with response caching where the id must be the same across requests to not bust the cache.
 *
 * @param url A URL for which to generate a stable requestId.
 * @returns A stable requestID
 */
export const getStableRequestId = cache((url: string) => getUIDHash(url));
